/**
 * Мутации.
 *
 * Каждая создаёт ключ идемпотентности один раз и переиспользует его при
 * повторах: клиент в метро отправляет запрос по три раза, сервер должен
 * увидеть одно действие.
 */

import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query'
import { useRef } from 'react'

import { ApiError, api, newIdempotencyKey } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queries'
import type { Role } from '@/shared/api/types'

export interface CreateFamilyInput {
  title?: string
  child_name?: string
  nanny_rate?: string
  timesheet_period?: 'week' | 'halfmonth' | 'month'
  timezone?: string
}

export interface InviteOut {
  id: number
  role: Role
  token: string
  link: string
  expires_at: string
  hourly_rate?: string | null
}

export interface CreateFamilyResult {
  family_id: number
  child_id: number | null
  invite: InviteOut
}

export interface InvitePreview {
  family_title: string
  role: Role
  inviter_name: string
  expires_at: string
  hourly_rate?: string | null
}

/**
 * Ключ живёт столько же, сколько компонент с формой: пользователь нажал
 * «Создать» пять раз подряд — на сервере это одно создание.
 */
function useStableKey(): () => string {
  const ref = useRef<string>()
  return () => {
    if (!ref.current) ref.current = newIdempotencyKey()
    return ref.current
  }
}

export function useCreateFamily(
  options?: UseMutationOptions<CreateFamilyResult, ApiError, CreateFamilyInput>,
) {
  const queryClient = useQueryClient()
  const key = useStableKey()

  return useMutation<CreateFamilyResult, ApiError, CreateFamilyInput>({
    mutationFn: (input) =>
      api.post<CreateFamilyResult>('/family', input, { idempotencyKey: key() }),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useAcceptInvite(
  options?: UseMutationOptions<{ family_id: number; role: Role }, ApiError, string>,
) {
  const queryClient = useQueryClient()
  const key = useStableKey()

  return useMutation<{ family_id: number; role: Role }, ApiError, string>({
    mutationFn: (token) =>
      api.post<{ family_id: number; role: Role }>(
        '/invites/accept',
        { token },
        { idempotencyKey: key() },
      ),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export interface CreateInviteInput {
  role: Role
  hourly_rate?: string
  overtime_after_min?: number
  overtime_rate?: string
  rounding_min?: number
}

export function useCreateInvite(
  options?: UseMutationOptions<InviteOut, ApiError, CreateInviteInput>,
) {
  return useMutation<InviteOut, ApiError, CreateInviteInput>({
    // ключ новый на каждый вызов: две ссылки подряд — осознанное действие
    mutationFn: (input) => api.post<InviteOut>('/invites', input),
    ...options,
  })
}

export interface SettingsInput {
  timesheet_period?: 'week' | 'halfmonth' | 'month'
  geo_enabled?: boolean
  expense_auto_approve_limit?: string | null
  quiet_hours_from?: string | null
  quiet_hours_to?: string | null
}

export function useUpdateSettings(options?: UseMutationOptions<unknown, ApiError, SettingsInput>) {
  const queryClient = useQueryClient()
  return useMutation<unknown, ApiError, SettingsInput>({
    mutationFn: (input) => api.patch('/family/settings', input),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export interface TariffInput {
  userId: number
  hourly_rate?: string
  overtime_after_min?: number | null
  overtime_rate?: string | null
  rounding_min?: number
}

export function useUpdateTariff(options?: UseMutationOptions<unknown, ApiError, TariffInput>) {
  const queryClient = useQueryClient()
  return useMutation<unknown, ApiError, TariffInput>({
    mutationFn: ({ userId, ...body }) => api.patch(`/family/members/${userId}/tariff`, body),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export interface ProfileInput {
  phone?: string | null
  bank_hint?: string | null
  tz?: string
}

export function useUpdateProfile(options?: UseMutationOptions<unknown, ApiError, ProfileInput>) {
  const queryClient = useQueryClient()
  return useMutation<unknown, ApiError, ProfileInput>({
    mutationFn: (input) => api.patch('/profile', input),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}
